import { Box } from "../../ui/box";
import { HStack } from "../../ui/hstack";
import { Icon } from "../../ui/icon";
import { Pressable } from "../../ui/pressable";
import { Text } from "../../ui/text";
import { Lock } from "lucide-react-native";

export interface SegmentedOption<T extends string> {
  key: T;
  label: string;
  badge?: number | string;
  sparkle?: boolean;
  /** Shows a lock next to the label (feature not included in the plan). */
  locked?: boolean;
}

interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (key: T) => void;
  /** Stretch the pills to split the track evenly (a two-tab toggle on a phone). */
  fill?: boolean;
}

/** Pill group on a grey track. The active pill (`aria-selected`) becomes a
 *  white surface with a soft shadow — selected styling is CSS-driven via
 *  `aria-selected` / `group-aria-selected` selectors, with no per-pill JS
 *  className branching. */
function Segmented<T extends string>({
  options,
  value,
  onChange,
  fill = false,
}: SegmentedProps<T>) {
  return (
    <HStack className="rounded-xl bg-background-100 p-1" space="xs">
      {options.map((opt) => (
        <Pressable
          key={opt.key}
          onPress={() => onChange(opt.key)}
          aria-selected={opt.key === value}
          accessibilityRole="tab"
          className={`group flex-row items-center gap-1.5 rounded-lg px-3 py-1.5 ${fill ? "flex-1 justify-center" : ""} aria-selected:bg-background-0 aria-selected:shadow-soft-1 transition-all`}
        >
          {opt.sparkle && (
            <Text size="sm" className="text-brand-500">
              ✦
            </Text>
          )}
          <Text
            size={"xs"}
            className="text-typography-500 group-aria-selected:font-bold group-aria-selected:text-typography-900 transition-all"
          >
            {opt.label}
          </Text>
          {opt.locked && (
            <Icon as={Lock} size="2xs" className="text-typography-400" />
          )}
          {opt.badge != null && (
            <Box className="rounded-full bg-background-200 px-1.5 py-0.5 group-aria-selected:bg-brand-50 transition-all">
              <Text
                size="2xs"
                bold
                className="tabular-nums text-typography-500 group-aria-selected:text-brand-600 transition-all"
              >
                {opt.badge}
              </Text>
            </Box>
          )}
        </Pressable>
      ))}
    </HStack>
  );
}

export default Segmented;
